// HormoneAddonPreview.jsx
// Sandbox preview — no auth, no API. Access at /HormoneAddonPreview
// Shows all 5 hormone states with realistic mock data + EN/DE toggle.

import React, { useState, useEffect } from 'react';
import i18next from 'i18next';
import { initReactI18next, I18nextProvider } from 'react-i18next';
import { resources } from './hormoneTranslations';
import HormoneAddonPage from './HormoneAddonPage';
import { tokens } from './hormoneData';

// ── Extend translations to fill key-gaps between component and translation file ──
// Component uses some keys that differ from what's in hormoneTranslations.js.
// We merge the missing ones here so nothing renders as a raw key string.

const buildTranslations = (lang) => {
  const base = resources[lang].hormone;
  return {
    ...base,
    page: {
      ...base.page,
      subtitle: lang === 'de'
        ? '10 Biomarker · Hormone & Fruchtbarkeit Add-on'
        : '10 biomarkers · Hormones & Fertility add-on',
      yourSummary: lang === 'de' ? 'Dein Hormonstatus' : 'Your hormone picture',
    },
    chart: {
      ...base.chart,
      dayLabel: lang === 'de' ? 'Tag' : 'Day',
      median:   lang === 'de' ? 'Medianwert' : 'Median',
      yourResult:     lang === 'de' ? 'Dein Ergebnis'     : 'Your result',
      previousResult: lang === 'de' ? 'Früheres Ergebnis' : 'Previous result',
      day:            lang === 'de' ? 'Tag' : 'Day',
      naturalMedian:  lang === 'de' ? 'Natürlicher Median' : 'Natural median',
      hrtMedian:      lang === 'de' ? 'Median mit HRT'     : 'HRT median',
      yourZone:       lang === 'de' ? 'Dein ungefährer Bereich' : 'Your approximate zone',
      tabs: {
        ...base.chart.tabs,
        connect: lang === 'de' ? 'Verbinden' : 'Connect',
      },
      // Component looks up chart.menoStages.<labelKey>; translations use chart.stages.*
      menoStages: {
        earlyPeri:   base.chart.stages.earlyPeri,
        latePeri:    base.chart.stages.latePeri,
        menopause:   base.chart.stages.menopause,
        postmeno:    base.chart.stages.postmenopause,
      },
    },
    // Component uses banners.contraception / banners.hrt / banners.irregularCycle as strings;
    // translations nest them as { title, body }. Remap to the body copy.
    banners: {
      ...base.banners,
      contraception:  base.banners.contraception.body,
      hrt:            base.banners.hrt.body,
      irregularCycle: base.banners.irregularCycle.body,
    },
    // Component reads bodyContext.cycle.<phase>.* and bodyContext.meno.<stage>.*;
    // translations use bodyContext.phases.* and bodyContext.stages.*
    bodyContext: {
      ...base.bodyContext,
      cycle: base.bodyContext.phases,
      meno:  base.bodyContext.stages,
    },
    markerSection: {
      ...base.markerSection,
      acrossLifespan:     lang === 'de' ? 'Im Lebensverlauf'         : 'Across your lifespan',
      symptomConnections: lang === 'de' ? 'Symptomzusammenhänge'     : 'Symptom connections',
      symptoms: {
        cycle:     lang === 'de' ? 'Zyklus'        : 'Cycle',
        fertility: lang === 'de' ? 'Fruchtbarkeit' : 'Fertility',
        libido:    lang === 'de' ? 'Libido'         : 'Libido',
        mood:      lang === 'de' ? 'Stimmung'       : 'Mood',
        energy:    lang === 'de' ? 'Energie'        : 'Energy',
      },
      fertilityNote: lang === 'de' ? '🌱 Fruchtbarkeitskontext' : '🌱 Fertility context',
      contraNote:    lang === 'de' ? '💊 Verhütungskontext'     : '💊 Contraception context',
      hrtNote:       lang === 'de' ? '💉 HRT-Kontext'           : '💉 HRT context',
    },
    status: {
      ...base.status,
      optimal: lang === 'de' ? 'Optimal' : 'Optimal',
    },
    doctor: {
      ...base.doctor,
      sectionLabel: lang === 'de' ? 'Für deinen Arzt'       : 'For your doctor',
      title:        lang === 'de' ? 'Deine Arztfrage'        : 'Your doctor question',
      hint:         lang === 'de'
        ? 'Kopiere diese Frage vor deinem Arzttermin in die Zwischenablage.'
        : 'Copy this to your clipboard before your appointment.',
    },
  };
};

// ── Dedicated i18next instance (isolated from app's custom i18n) ──
const hormoneI18n = i18next.createInstance();
hormoneI18n.use(initReactI18next).init({
  lng: 'en',
  fallbackLng: 'en',
  ns: ['hormone'],
  defaultNS: 'hormone',
  resources: {
    en: { hormone: buildTranslations('en') },
    de: { hormone: buildTranslations('de') },
  },
  interpolation: { escapeValue: false },
});

// ── Shared hormone results ────────────────────────────────────────────────────
const BASE_RESULTS = [
  {
    markerId: 'LH',
    value: 28.4,
    unit: 'mIE/mL',
    referenceRangeLow: 2.4,
    referenceRangeHigh: 12.6,
    status: 'high',
    phaseAdjustedStatus: 'high',
    historicalValues: [
      { date: '2024-12-01', cycleDay: 13, value: 24.1 },
      { date: '2024-09-01', cycleDay: 14, value: 31.2 },
    ],
  },
  {
    markerId: 'FSH',
    value: 7.8,
    unit: 'mIE/mL',
    referenceRangeLow: 3.5,
    referenceRangeHigh: 12.5,
    status: 'normal',
    phaseAdjustedStatus: null,
    historicalValues: [{ date: '2024-12-01', cycleDay: 13, value: 8.1 }],
  },
  {
    markerId: 'Prolactin',
    value: 12.4,
    unit: 'ng/mL',
    referenceRangeLow: 2.0,
    referenceRangeHigh: 29.7,
    status: 'normal',
    phaseAdjustedStatus: null,
    historicalValues: [],
  },
  {
    markerId: 'E2',
    value: 412,
    unit: 'pg/mL',
    referenceRangeLow: 27,
    referenceRangeHigh: 470,
    status: 'normal',
    phaseAdjustedStatus: 'optimal',
    historicalValues: [
      { date: '2024-12-01', cycleDay: 13, value: 388 },
      { date: '2024-09-01', cycleDay: 14, value: 445 },
    ],
  },
  {
    markerId: 'P4',
    value: 1.1,
    unit: 'nmol/L',
    referenceRangeLow: 0.5,
    referenceRangeHigh: 3.0,
    status: 'normal',
    phaseAdjustedStatus: null,
    historicalValues: [{ date: '2024-12-01', cycleDay: 13, value: 0.9 }],
  },
  {
    markerId: 'Testosterone',
    value: 38,
    unit: 'ng/dL',
    referenceRangeLow: 15,
    referenceRangeHigh: 70,
    status: 'normal',
    phaseAdjustedStatus: null,
    historicalValues: [],
  },
  {
    markerId: 'DHEAS',
    value: 220,
    unit: 'µg/dL',
    referenceRangeLow: 85,
    referenceRangeHigh: 380,
    status: 'normal',
    phaseAdjustedStatus: null,
    historicalValues: [],
  },
  {
    markerId: 'SHBG',
    value: 58,
    unit: 'nmol/L',
    referenceRangeLow: 17,
    referenceRangeHigh: 120,
    status: 'normal',
    phaseAdjustedStatus: null,
    historicalValues: [],
  },
  {
    markerId: 'BioT',
    value: 11.2,
    unit: 'pg/mL',
    referenceRangeLow: 6,
    referenceRangeHigh: 42,
    status: 'normal',
    phaseAdjustedStatus: null,
    historicalValues: [],
  },
  {
    markerId: 'TE2ratio',
    value: 0.092,
    unit: '',
    referenceRangeLow: 0.05,
    referenceRangeHigh: 0.30,
    status: 'normal',
    phaseAdjustedStatus: null,
    historicalValues: [],
  },
];

// ── State-specific mock datasets ──────────────────────────────────────────────
const MOCK_DATA = {
  natural_cycle: {
    user: {
      age: 28,
      hormoneState: 'natural_cycle',
      cycleDay: 14,
      cycleLength: 28,
      onHRT: false,
      hrtType: null,
      onContraception: false,
      contraceptionType: null,
      hasCycleTracker: true,
      irregularCycles: false,
    },
    hormoneResults: BASE_RESULTS,
    questionnaire: {
      symptoms: ['mood_swings', 'bloating'],
      mood: 'good',
      energy: 'normal',
      libido: 'high',
    },
    aiNarrativeText:
      'Your estradiol is peaking beautifully at ovulation — exactly where it should be on cycle day 14. Your LH is elevated because your pituitary just fired the ovulation signal, which is completely expected today. Progesterone remains low, which is normal before ovulation has completed. Overall, your cycle is showing a well-timed mid-cycle pattern consistent with healthy ovulatory function.',
    aiDoctorQuestion:
      "My FSH came back at 7.8 mIE/mL on cycle day 14. Could we check a day 2–3 FSH at my next visit to get a cleaner read on ovarian reserve, given that I'm 28 and thinking about timing for a pregnancy in the next few years?",
  },

  hormonal_contraception: {
    user: {
      age: 25,
      hormoneState: 'hormonal_contraception',
      cycleDay: 10,
      cycleLength: 28,
      onHRT: false,
      hrtType: null,
      onContraception: true,
      contraceptionType: 'Combined oral pill (Yasmin)',
      hasCycleTracker: false,
      irregularCycles: false,
    },
    hormoneResults: BASE_RESULTS.map((r) => {
      if (r.markerId === 'LH')   return { ...r, value: 1.2,  status: 'low'  };
      if (r.markerId === 'FSH')  return { ...r, value: 2.1,  status: 'low'  };
      if (r.markerId === 'P4')   return { ...r, value: 0.3,  status: 'low'  };
      if (r.markerId === 'SHBG') return { ...r, value: 185,  status: 'high' };
      if (r.markerId === 'BioT') return { ...r, value: 3.4,  status: 'low'  };
      return r;
    }),
    questionnaire: {
      symptoms: ['low_libido', 'low_energy'],
      mood: 'neutral',
      energy: 'low',
      libido: 'low',
    },
    aiNarrativeText:
      'Your results look exactly as expected for someone on a combined oral pill — LH and FSH are suppressed, which is the mechanism that prevents ovulation. What stands out is your SHBG at 185 nmol/L, significantly elevated. This level of SHBG binds most of your available testosterone, leaving very little bioavailable — a pattern that directly explains the low libido and low energy you reported.',
    aiDoctorQuestion: null,
  },

  perimenopause: {
    user: {
      age: 46,
      hormoneState: 'perimenopause',
      cycleDay: 18,
      cycleLength: 32,
      onHRT: false,
      hrtType: null,
      onContraception: false,
      contraceptionType: null,
      hasCycleTracker: true,
      irregularCycles: true,
    },
    hormoneResults: BASE_RESULTS.map((r) => {
      if (r.markerId === 'FSH')         return { ...r, value: 18.2, status: 'high' };
      if (r.markerId === 'E2')          return { ...r, value: 88,   status: 'low', phaseAdjustedStatus: 'low' };
      if (r.markerId === 'P4')          return { ...r, value: 4.2,  status: 'low' };
      if (r.markerId === 'Testosterone') return { ...r, value: 22,  status: 'low' };
      return r;
    }),
    questionnaire: {
      symptoms: ['mood_swings', 'sleep_issues', 'hot_flashes', 'brain_fog'],
      mood: 'anxious',
      energy: 'very_low',
      libido: 'low',
    },
    aiNarrativeText:
      "Your FSH at 18.2 mIE/mL alongside the irregular cycles you're experiencing is a clear biological signal that you're entering perimenopause. Your estradiol of 88 pg/mL is lower than the typical mid-cycle range — and in perimenopause, it's the unpredictable fluctuation of estradiol, not just the overall level, that drives symptoms like mood instability, sleep disruption, and brain fog that you've described. Progesterone is also lower than expected for the luteal phase, which is common when ovulation becomes inconsistent.",
    aiDoctorQuestion:
      "I'm 46 with recently irregular cycles and my FSH is 18.2 mIE/mL. I'm experiencing significant sleep disruption, mood instability, and brain fog — could we discuss whether these are perimenopausal symptoms and whether hormonal support is appropriate at this stage of my transition?",
  },

  menopause: {
    user: {
      age: 54,
      hormoneState: 'menopause',
      cycleDay: null,
      cycleLength: 28,
      onHRT: false,
      hrtType: null,
      onContraception: false,
      contraceptionType: null,
      hasCycleTracker: false,
      irregularCycles: false,
    },
    hormoneResults: BASE_RESULTS.map((r) => {
      if (r.markerId === 'LH')   return { ...r, value: 42.1, status: 'high' };
      if (r.markerId === 'FSH')  return { ...r, value: 68.4, status: 'high' };
      if (r.markerId === 'E2')   return { ...r, value: 18,   status: 'low'  };
      if (r.markerId === 'P4')   return { ...r, value: 0.4,  status: 'low'  };
      if (r.markerId === 'Testosterone') return { ...r, value: 16, status: 'low' };
      if (r.markerId === 'BioT') return { ...r, value: 4.8,  status: 'low'  };
      return r;
    }),
    questionnaire: {
      symptoms: ['hot_flashes', 'low_libido', 'joint_pain', 'sleep_issues'],
      mood: 'low',
      energy: 'low',
      libido: 'very_low',
    },
    aiNarrativeText:
      'Your FSH at 68.4 mIE/mL and LH at 42.1 mIE/mL confirm a fully established postmenopausal profile. Estradiol at 18 pg/mL is in the low postmenopausal range, which directly explains the hot flashes, sleep disruption, and joint pain you\'re experiencing. Your bioavailable testosterone is also low at 4.8 pg/mL — a frequently overlooked contributor to the very low libido and energy you reported.',
    aiDoctorQuestion:
      "I'm 54, fully postmenopausal, with an FSH of 68.4 and E2 of 18 pg/mL. I'm experiencing significant hot flashes, joint pain, sleep disruption, and very low libido — could we have a proper conversation about whether HRT is appropriate for me, including the current evidence on risk and benefit for my age and health profile?",
  },

  menopause_HRT: {
    user: {
      age: 57,
      hormoneState: 'menopause_HRT',
      cycleDay: null,
      cycleLength: 28,
      onHRT: true,
      hrtType: 'Transdermal estradiol 75 µg + micronized progesterone 200 mg',
      onContraception: false,
      contraceptionType: null,
      hasCycleTracker: false,
      irregularCycles: false,
    },
    hormoneResults: BASE_RESULTS.map((r) => {
      if (r.markerId === 'LH')   return { ...r, value: 22.4, status: 'high'   };
      if (r.markerId === 'FSH')  return { ...r, value: 28.8, status: 'high'   };
      if (r.markerId === 'E2')   return { ...r, value: 92,   status: 'normal' };
      if (r.markerId === 'P4')   return { ...r, value: 18.4, status: 'normal' };
      if (r.markerId === 'Testosterone') return { ...r, value: 24, status: 'low' };
      if (r.markerId === 'SHBG') return { ...r, value: 42,   status: 'normal' };
      if (r.markerId === 'BioT') return { ...r, value: 8.6,  status: 'normal' };
      return r;
    }),
    questionnaire: {
      symptoms: ['low_libido', 'low_energy'],
      mood: 'neutral',
      energy: 'normal',
      libido: 'low',
    },
    aiNarrativeText:
      'Your HRT is delivering a good estradiol level at 92 pg/mL — squarely in the therapeutic range for symptom control and bone protection. Progesterone at 18.4 nmol/L reflects your micronized progesterone providing effective uterine protection. Your FSH remains elevated (28.8), which is normal on transdermal estrogen. The one gap is testosterone: at 24 ng/dL with a low bioavailable fraction, this may be driving the persistent low libido and energy despite otherwise well-controlled estrogen levels.',
    aiDoctorQuestion:
      "I'm 57 on transdermal estradiol 75 µg and micronized progesterone, with E2 at 92 pg/mL and P4 at 18.4 nmol/L — my estrogen HRT looks effective. But my total testosterone is 24 ng/dL with low bioavailable T, and I still have significant low libido and fatigue. Could we discuss whether adding low-dose testosterone supplementation is appropriate and what the current evidence looks like?",
  },
};

// ── State selector config ─────────────────────────────────────────────────────
const STATES = [
  { id: 'natural_cycle',          label: 'Natural Cycle',    emoji: '🌸' },
  { id: 'hormonal_contraception', label: 'Contraception',    emoji: '💊' },
  { id: 'perimenopause',          label: 'Perimenopause',    emoji: '🌀' },
  { id: 'menopause',              label: 'Menopause',        emoji: '🍂' },
  { id: 'menopause_HRT',          label: 'Menopause + HRT',  emoji: '⚕️' },
];

// ── Preview wrapper ───────────────────────────────────────────────────────────
export default function HormoneAddonPreview() {
  const [selectedState, setSelectedState] = useState('natural_cycle');
  const [lang, setLang] = useState('en');
  const [ready, setReady] = useState(hormoneI18n.isInitialized);

  useEffect(() => {
    if (!hormoneI18n.isInitialized) {
      hormoneI18n.on('initialized', () => setReady(true));
    }
  }, []);

  const handleLang = (l) => {
    setLang(l);
    hormoneI18n.changeLanguage(l);
  };

  const { user, hormoneResults, questionnaire, aiNarrativeText, aiDoctorQuestion } =
    MOCK_DATA[selectedState];

  if (!ready) return null;

  return (
    <div>
      {/* ── Controls bar ── */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 1000,
        display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
        padding: '10px 16px',
        background: tokens.bgCard,
        borderBottom: `1px solid ${tokens.border}`,
        boxShadow: tokens.shadow,
      }}>
        <span style={{
          fontSize: 11, fontWeight: 700, color: tokens.textMuted,
          letterSpacing: '0.06em', textTransform: 'uppercase', flexShrink: 0,
        }}>
          State
        </span>
        {STATES.map((s) => (
          <button
            key={s.id}
            onClick={() => setSelectedState(s.id)}
            style={{
              padding: '5px 12px', borderRadius: 99,
              fontSize: 12, fontWeight: selectedState === s.id ? 700 : 400,
              cursor: 'pointer',
              border: `1.5px solid ${selectedState === s.id ? tokens.teal : tokens.border}`,
              background: selectedState === s.id ? tokens.tealLight : 'transparent',
              color: selectedState === s.id ? tokens.teal : tokens.textSecondary,
              transition: 'all 0.15s',
            }}
          >
            {s.emoji} {s.label}
          </button>
        ))}

        <div style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
          {['en', 'de'].map((l) => (
            <button
              key={l}
              onClick={() => handleLang(l)}
              style={{
                padding: '4px 10px', borderRadius: 6,
                fontSize: 12, fontWeight: lang === l ? 700 : 400,
                cursor: 'pointer',
                border: `1px solid ${lang === l ? tokens.teal : tokens.border}`,
                background: lang === l ? tokens.tealLight : 'transparent',
                color: lang === l ? tokens.teal : tokens.textSecondary,
              }}
            >
              {l.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {/* ── Component under preview ── */}
      <I18nextProvider i18n={hormoneI18n}>
        <HormoneAddonPage
          user={user}
          hormoneResults={hormoneResults}
          questionnaire={questionnaire}
          aiNarrativeText={aiNarrativeText}
          aiDoctorQuestion={aiDoctorQuestion}
        />
      </I18nextProvider>
    </div>
  );
}
